import { useState, useEffect, useMemo } from 'react';
import { usePlaceholder } from '../../hooks/usePlaceholder';
import { X, Search, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Universal Select Modal - Handles all select modals with pagination
 * Only renders when isOpen === true (lazy rendering for performance)
 * 
 * @param {Object} config - Modal configuration
 * @param {Array} config.items - Array of items to display
 * @param {boolean} config.isOpen - Whether modal is open
 * @param {Function} config.onClose - Close handler
 * @param {Function} config.onSelect - Selection handler (receives selected item)
 * @param {Object|string} config.selectedItem - Currently selected item (for highlighting)
 * @param {string} config.title - Modal title
 * @param {boolean} config.isSpanish - Language flag
 * @param {Object} config.fields - Field mappings: { primary, secondary, searchFields, sortFields }
 * @param {Array} config.sortOptions - Array of { value, label } sort options
 * @param {string} config.defaultSort - Default sort value
 * @param {string} config.maxWidth - Tailwind max-width class (default: 'max-w-md')
 * @param {number} config.itemsPerPage - Items per page (default: 50)
 * @param {string} config.searchPlaceholderType - Placeholder type for usePlaceholder hook
 * @param {string} config.displayFormat - Display format: 'primary-secondary', 'code-name', 'name-code', 'code-only'
 */
export const UniversalSelectModal = ({
  items = [],
  isOpen,
  onClose,
  onSelect,
  selectedItem,
  title = 'Select Item',
  isSpanish = false,
  fields = {
    primary: 'nombre',      // Main field to display
    secondary: 'codigo',      // Secondary field to display
    searchFields: ['nombre', 'codigo'], // Fields to search in
    sortFields: {            // Field mappings for sorting
      'name-asc': 'nombre',
      'name-desc': 'nombre',
      'code-asc': 'codigo',
      'code-desc': 'codigo',
    }
  },
  sortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
    { value: 'code-asc', label: isSpanish ? 'Código (A-Z)' : 'Code (A-Z)' },
    { value: 'code-desc', label: isSpanish ? 'Código (Z-A)' : 'Code (Z-A)' },
  ],
  defaultSort = 'name-asc',
  maxWidth = 'max-w-md',
  itemsPerPage = 50,
  searchPlaceholderType = 'search_generic',
  displayFormat = 'primary-secondary', // 'primary-secondary', 'code-name', 'name-code', 'code-only'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(defaultSort);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const searchPlaceholder = usePlaceholder({ isSpanish, type: searchPlaceholderType });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchQuery('');
      setIsSortOpen(false);
    }
  }, [isOpen]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter((item) => {
      return fields.searchFields.some((field) => {
        const value = item[field];
        return value && String(value).toLowerCase().includes(query);
      });
    });
  }, [items, searchQuery, fields.searchFields]);

  // Sort filtered items
  const sortedItems = useMemo(() => {
    const sortField = fields.sortFields?.[sortBy];
    if (!sortField) return [...filteredItems];

    const isDesc = sortBy.includes('-desc');
    const sorted = [...filteredItems].sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      const comparison = String(aVal).localeCompare(String(bVal));
      return isDesc ? -comparison : comparison;
    });
    return sorted;
  }, [filteredItems, sortBy, fields.sortFields]);

  // Paginate sorted items
  const totalItems = sortedItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = sortedItems.slice(startIndex, endIndex);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy]);

  const getSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || 
           (isSpanish ? 'Ordenar por' : 'Sort by');
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  // Check if item is selected
  const isItemSelected = (item) => {
    if (!selectedItem) return false;
    
    // Handle object comparison
    if (typeof selectedItem === 'object') {
      const primaryMatch = fields.primary && selectedItem[fields.primary] === item[fields.primary];
      const secondaryMatch = fields.secondary && selectedItem[fields.secondary] === item[fields.secondary];
      return primaryMatch || secondaryMatch || selectedItem === item;
    }
    
    // Handle string/number comparison
    return selectedItem === item[fields.primary] || 
           selectedItem === item[fields.secondary] ||
           selectedItem === item.codigo ||
           selectedItem === item.iso;
  };

  // Render item content based on display format
  const renderItemContent = (item) => {
    const primary = item[fields.primary];
    const secondary = item[fields.secondary];
    
    switch (displayFormat) {
      case 'code-name':
        return (
          <>
            <span className="font-bold text-lg">{item.codigo || primary}</span>
            {secondary && <p className="text-sm text-gray-700">{secondary}</p>}
          </>
        );
      case 'name-code':
        return (
          <>
            <p className="font-medium">{primary}</p>
            {secondary && <p className="text-xs text-gray-500">{secondary}</p>}
          </>
        );
      case 'code-only':
        return <p className="font-medium">{item.codigo || primary}</p>;
      case 'primary-secondary':
      default:
        return (
          <>
            <p className="font-medium">{primary}</p>
            {secondary && <p className="text-xs text-gray-500">{secondary}</p>}
          </>
        );
    }
  };

  // CRITICAL: Don't render anything if closed (lazy rendering for performance) This prevents hooks from running when modal is closed
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div className={`bg-white rounded-lg p-6 w-full ${maxWidth} mx-4 animate-modalEnter`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={() => {
              setSearchQuery('');
              setIsSortOpen(false);
              onClose();
            }}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {/* Search Input */}
          <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1 flex-row">
            <div className="flex-0 pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="pr-4 py-2 !border-none no-border search-input"
              style={{ borderWidth: '0px !important', border: '0px !important' }}
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect text-xs">
            <button
              className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>

            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
          {items.length > 0 ? (
            <div className="space-y-0">
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item) => {
                  const key = item.codigo || item.iso || item[fields.primary] || JSON.stringify(item);
                  return (
                    <div
                      key={key}
                      className={`p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray ${
                        isItemSelected(item) ? 'bg-gray-100 font-bold' : ''
                      }`}
                      onClick={() => {
                        onSelect(item);
                        setSearchQuery('');
                        setIsSortOpen(false);
                        onClose();
                      }}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1">
                          {renderItemContent(item)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {isSpanish 
                    ? `No se encontraron resultados que coincidan con "${searchQuery}"`
                    : `No results found matching "${searchQuery}"`}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className='opacity-75 mb-4'>
                {isSpanish ? 'No hay elementos disponibles' : 'No items available'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center mt-4 justify-between bg-gray-100 rounded-full p-0 px-4 pr-0">
            <div className="text-xs font-semibold text-gray-600">
              {isSpanish 
                ? `Mostrando ${startIndex + 1}-${Math.min(endIndex, totalItems)} de ${totalItems}`
                : `Showing ${startIndex + 1}-${Math.min(endIndex, totalItems)} of ${totalItems}`}
            </div>
            <div className="flex items-center gap-2 bg-gray-200 rounded-full p-1.5 px-3">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-1.5 py-1.5 text-sm rounded-full ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-600">
                {isSpanish ? `Página ${currentPage} de ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-1.5 py-1.5 text-sm rounded-full ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

